import React, { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Loader2, TriangleAlert } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useShadcnInsetDynamicPageTitle } from "@/hooks/useShadcnInsetBreadcrumbTrail";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { isWorkspaceEnabledForUser } from "@/utils/workspaceFeatureFlag";
import { getFederatedWorkspace } from "../../../services/reportsApi";
import { federatedWorkspaceToDetail } from "../../../services/workspacesApi";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import { WorkspaceCanvasOverlays } from "./WorkspaceCanvasOverlays";
import { WorkspaceErrorBoundary } from "./WorkspaceErrorBoundary";
import {
  WorkspaceProvider,
  useWorkspaceContext,
  type WorkspaceLoadResult,
} from "./workspaceContext";
import { useWorkspaceAccountScope } from "./useWorkspaceAccountScope";
import { WORKSPACE_HUB_BREADCRUMB } from "./workspaceRoutes";

/** Placeholder scope — Strategy view never POSTs against federated ids. */
const STRATEGY_SCOPE_FALLBACK: WorkspaceAccountScope = {
  brokerName: "liquidbrokers",
  brokerAccountCode: "strategy-view",
};

function WorkspaceStrategyCanvasContent(): JSX.Element {
  const { detail, loading, error, refreshDetail, strategyName } = useWorkspaceContext();
  const pageMinHeight = statisticsPageMinHeight(useShadcnInsetChrome());

  useShadcnInsetDynamicPageTitle(strategyName?.trim() || "Strategy workspace", {
    parent: WORKSPACE_HUB_BREADCRUMB,
  });

  useEffect(() => {
    void refreshDetail();
  }, [refreshDetail]);

  if (loading && !detail) {
    return (
      <div
        data-workspace-strategy-canvas-page=""
        className="flex w-full flex-col items-center justify-center gap-3 py-16"
      >
        <Loader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
        <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
          Loading strategy workspace…
        </p>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div
        data-workspace-strategy-canvas-page=""
        className="flex w-full flex-col items-center justify-center px-4 py-16"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="no-data"
          title="Strategy workspace unavailable"
          description={error}
        />
      </div>
    );
  }

  return (
    <div data-workspace-strategy-canvas-page="" className="flex min-h-0 w-full flex-1 flex-col">
      {error ? (
        <Alert variant="warning" className="m-4 mb-0 shrink-0">
          <TriangleAlert />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <WorkspaceErrorBoundary onReset={() => void refreshDetail()}>
        <WorkspaceCanvasOverlays />
      </WorkspaceErrorBoundary>
      <span className="sr-only">{strategyName ? `Strategy workspace: ${strategyName}` : null}</span>
    </div>
  );
}

export default function WorkspaceStrategyCanvasPage(): JSX.Element {
  const { strategyId } = useParams<{ strategyId: string }>();
  const { scope, ready, loading: accountLoading } = useWorkspaceAccountScope();
  const { isAdmin, isLoading: flagsLoading } = useFeatureFlags();
  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const trimmedStrategyId = strategyId?.trim() || "";

  const loadDetail = useCallback(
    async (_scope: WorkspaceAccountScope, _workspaceId: string): Promise<WorkspaceLoadResult> => {
      const fed = await getFederatedWorkspace(trimmedStrategyId);
      return {
        detail: federatedWorkspaceToDetail(fed),
        strategyName: fed.strategyName,
        contributingWorkspaces: fed.contributingWorkspaces,
      };
    },
    [trimmedStrategyId]
  );

  if (!flagsLoading && !isWorkspaceEnabledForUser(isAdmin)) {
    return (
      <div
        data-workspace-strategy-canvas-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="no-data"
          title="Workspace not available"
          description="Workspace is not enabled for your account yet."
        />
      </div>
    );
  }

  if (accountLoading || flagsLoading) {
    return (
      <div
        data-workspace-strategy-canvas-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="loading"
          title="Loading…"
          description="Preparing strategy workspace."
        />
      </div>
    );
  }

  if (!trimmedStrategyId) {
    return (
      <div
        data-workspace-strategy-canvas-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="no-data"
          title="Strategy workspace unavailable"
          description="Missing strategy id."
        />
      </div>
    );
  }

  const effectiveScope: WorkspaceAccountScope =
    ready && scope
      ? scope
      : STRATEGY_SCOPE_FALLBACK;

  return (
    <WorkspaceProvider
      scope={effectiveScope}
      workspaceId={`strategy:${trimmedStrategyId}`}
      viewMode="strategy"
      strategyId={trimmedStrategyId}
      loadDetail={loadDetail}
    >
      <WorkspaceStrategyCanvasContent />
    </WorkspaceProvider>
  );
}
